import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { WorkflowDetalhe, WorkflowEtapa, WorkflowEtapaTipo } from '../../core/models/workflow.models';
import { WorkflowsService } from '../../core/services/workflows.service';

type SlaStatusFilter = 'TODOS' | 'EM_ANDAMENTO' | 'CONCLUIDO' | 'ATRASADO';

interface GargaloItem {
  etapa: string;
  wip: number;
  atrasados: number;
  atrasoMedio: number;
}

@Component({
  selector: 'app-relatorios-gestao-sla-producao',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './relatorios-gestao-sla-producao.component.html',
  styleUrl: './relatorios-gestao-sla-producao.component.scss',
})
export class RelatoriosGestaoSlaProducaoComponent implements OnInit, OnDestroy {
  workflows: WorkflowDetalhe[] = [];
  filtered: WorkflowDetalhe[] = [];
  gargalos: GargaloItem[] = [];
  tableItems: WorkflowDetalhe[] = [];

  filterStart = '';
  filterEnd = '';
  filterSeguradora = 'TODOS';
  filterResponsavel = 'TODOS';
  filterStatus: SlaStatusFilter = 'TODOS';

  seguradoras: string[] = [];
  responsaveis: string[] = [];

  total = 0;
  entreguesNoPrazo = 0;
  entreguesAtraso = 0;
  percentNoPrazo = 0;
  atrasoMedio = 0;
  leadTimeMedio = 0;
  activeTab: 'gargalos' | 'lista' = 'gargalos';

  private subscription = new Subscription();

  constructor(private workflowsService: WorkflowsService) {}

  ngOnInit(): void {
    this.subscription.add(
      this.workflowsService.getAllDetails().subscribe((workflows) => {
        this.workflows = workflows;
        this.seguradoras = this.buildList((wf) => wf.seguradoraNome);
        this.responsaveis = this.buildList((wf) => wf.responsavelAtual);
        this.applyFilters();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  applyFilters(): void {
    const start = this.filterStart ? new Date(`${this.filterStart}T00:00:00`) : null;
    const end = this.filterEnd ? new Date(`${this.filterEnd}T23:59:59`) : null;

    this.filtered = this.workflows.filter((wf) => {
      const inicio = this.getDataInicio(wf);
      if (start && inicio < start) return false;
      if (end && inicio > end) return false;

      if (this.filterSeguradora !== 'TODOS' && (wf.seguradoraNome ?? '—') !== this.filterSeguradora) {
        return false;
      }
      if (this.filterResponsavel !== 'TODOS' && (wf.responsavelAtual ?? '—') !== this.filterResponsavel) {
        return false;
      }
      if (this.filterStatus !== 'TODOS' && !this.matchStatus(wf, this.filterStatus)) {
        return false;
      }
      return true;
    });

    this.rebuildSummary();
  }

  private rebuildSummary(): void {
    const now = new Date();
    this.total = this.filtered.length;

    const entregues = this.filtered.filter((wf) => this.isFinalizado(wf));
    const entreguesNoPrazo = entregues.filter((wf) => this.isEntregueNoPrazo(wf));
    const entreguesAtraso = entregues.filter((wf) => this.isEntregueComAtraso(wf));
    const emAtraso = this.filtered.filter((wf) => this.isAtrasadoEmAndamento(wf, now));

    this.entreguesNoPrazo = entreguesNoPrazo.length;
    this.entreguesAtraso = entreguesAtraso.length;
    const totalEntregues = entregues.length;
    this.percentNoPrazo = totalEntregues ? (this.entreguesNoPrazo / totalEntregues) * 100 : 0;

    const atrasosEntregues = entreguesAtraso
      .map((wf) => {
        const prevista = this.getDataFinalPrevista(wf);
        const real = this.getDataFinalReal(wf);
        if (!prevista || !real) return 0;
        return Math.max(0, Math.floor((real.getTime() - prevista.getTime()) / (24 * 60 * 60 * 1000)));
      })
      .filter((dias) => dias > 0);
    const atrasosEmAndamento = emAtraso
      .map((wf) => {
        const prevista = this.getDataFinalPrevista(wf);
        if (!prevista) return 0;
        return Math.max(0, Math.floor((now.getTime() - prevista.getTime()) / (24 * 60 * 60 * 1000)));
      })
      .filter((dias) => dias > 0);
    const atrasos = [...atrasosEntregues, ...atrasosEmAndamento];
    this.atrasoMedio = atrasos.length
      ? Math.round(atrasos.reduce((acc, value) => acc + value, 0) / atrasos.length)
      : 0;

    const leadTimes = entregues
      .map((wf) => {
        const inicio = this.getDataInicio(wf);
        const real = this.getDataFinalReal(wf);
        if (!real) return null;
        return Math.max(0, Math.floor((real.getTime() - inicio.getTime()) / (24 * 60 * 60 * 1000)));
      })
      .filter((value): value is number => value !== null);
    this.leadTimeMedio = leadTimes.length
      ? Math.round(leadTimes.reduce((acc, value) => acc + value, 0) / leadTimes.length)
      : 0;

    this.gargalos = this.buildGargalos(this.filtered);
    this.tableItems = [...this.filtered].sort((a, b) => {
      const atrasoDiff = this.getAtrasoDias(b, now) - this.getAtrasoDias(a, now);
      if (atrasoDiff !== 0) return atrasoDiff;
      const prevA = this.getDataFinalPrevista(a)?.getTime() ?? 0;
      const prevB = this.getDataFinalPrevista(b)?.getTime() ?? 0;
      return prevA - prevB;
    });
  }

  private buildGargalos(workflows: WorkflowDetalhe[]): GargaloItem[] {
    const now = new Date();
    const map = new Map<string, { wip: number; atrasados: number; atrasoTotal: number }>();

    workflows.forEach((wf) => {
      const etapa = this.getEtapaAtualLabel(wf);
      if (!map.has(etapa)) {
        map.set(etapa, { wip: 0, atrasados: 0, atrasoTotal: 0 });
      }
      const entry = map.get(etapa)!;
      entry.wip += 1;
      const atraso = this.isAtrasadoEmAndamento(wf, now) ? this.getAtrasoDias(wf, now) : 0;
      if (atraso > 0) {
        entry.atrasados += 1;
        entry.atrasoTotal += atraso;
      }
    });

    return Array.from(map.entries())
      .map(([etapa, stats]) => ({
        etapa,
        wip: stats.wip,
        atrasados: stats.atrasados,
        atrasoMedio: stats.atrasados ? Math.round(stats.atrasoTotal / stats.atrasados) : 0,
      }))
      .sort((a, b) => b.atrasados - a.atrasados);
  }

  private buildList(getter: (wf: WorkflowDetalhe) => string | undefined): string[] {
    const set = new Set<string>();
    this.workflows.forEach((wf) => {
      const value = getter(wf);
      if (value && value.trim()) {
        set.add(value.trim());
      }
    });
    return Array.from(set).sort();
  }

  matchStatus(wf: WorkflowDetalhe, filter: SlaStatusFilter): boolean {
    if (filter === 'EM_ANDAMENTO') {
      return !this.isFinalizado(wf) && !this.isAtrasadoEmAndamento(wf);
    }
    if (filter === 'CONCLUIDO') {
      return this.isFinalizado(wf);
    }
    if (filter === 'ATRASADO') {
      return this.isEntregueComAtraso(wf) || this.isAtrasadoEmAndamento(wf);
    }
    return true;
  }

  getDataInicio(wf: WorkflowDetalhe): Date {
    return wf.criadoEm instanceof Date ? wf.criadoEm : new Date(wf.criadoEm);
  }

  getDataFinalPrevista(wf: WorkflowDetalhe): Date | null {
    if (wf.dataPrevistaEntrega) {
      return wf.dataPrevistaEntrega instanceof Date
        ? wf.dataPrevistaEntrega
        : new Date(wf.dataPrevistaEntrega);
    }
    const dates = wf.etapas
      .map((etapa) => etapa.previstaFim)
      .filter((date): date is Date => Boolean(date));
    if (!dates.length) {
      return null;
    }
    return new Date(Math.max(...dates.map((date) => date.getTime())));
  }

  getDataFinalReal(wf: WorkflowDetalhe): Date | null {
    const finalizado = wf.etapas.find((etapa) => etapa.tipo === WorkflowEtapaTipo.FINALIZADO);
    if (finalizado?.fim) {
      return finalizado.fim instanceof Date ? finalizado.fim : new Date(finalizado.fim);
    }
    if (wf.etapaAtual === WorkflowEtapaTipo.FINALIZADO && wf.etapas.length) {
      const lastFim = wf.etapas[wf.etapas.length - 1].fim;
      if (lastFim) {
        return lastFim instanceof Date ? lastFim : new Date(lastFim);
      }
    }
    return null;
  }

  isFinalizado(wf: WorkflowDetalhe): boolean {
    return wf.etapaAtual === WorkflowEtapaTipo.FINALIZADO || Boolean(this.getDataFinalReal(wf));
  }

  isAtrasadoEmAndamento(wf: WorkflowDetalhe, now = new Date()): boolean {
    const prevista = this.getDataFinalPrevista(wf);
    const real = this.getDataFinalReal(wf);
    return Boolean(prevista && !real && now > prevista);
  }

  isEntregueNoPrazo(wf: WorkflowDetalhe): boolean {
    const prevista = this.getDataFinalPrevista(wf);
    const real = this.getDataFinalReal(wf);
    return Boolean(real && prevista && real.getTime() <= prevista.getTime());
  }

  isEntregueComAtraso(wf: WorkflowDetalhe): boolean {
    const prevista = this.getDataFinalPrevista(wf);
    const real = this.getDataFinalReal(wf);
    return Boolean(real && prevista && real.getTime() > prevista.getTime());
  }

  getAtrasoDias(wf: WorkflowDetalhe, now = new Date()): number {
    const prevista = this.getDataFinalPrevista(wf);
    if (!prevista) return 0;
    const real = this.getDataFinalReal(wf);
    const diff = (real ?? now).getTime() - prevista.getTime();
    return Math.max(0, Math.floor(diff / (24 * 60 * 60 * 1000)));
  }

  getLeadTimeDias(wf: WorkflowDetalhe, now = new Date()): number {
    const inicio = this.getDataInicio(wf);
    const real = this.getDataFinalReal(wf);
    const diff = (real ?? now).getTime() - inicio.getTime();
    return Math.max(0, Math.floor(diff / (24 * 60 * 60 * 1000)));
  }

  getEtapaAtualLabel(wf: WorkflowDetalhe): string {
    const etapa = wf.etapas.find((item) => item.tipo === wf.etapaAtual);
    return etapa?.nome ?? String(wf.etapaAtual);
  }

  getStatusSla(wf: WorkflowDetalhe): string {
    if (this.isFinalizado(wf)) {
      if (this.isEntregueComAtraso(wf)) {
        return 'Finalizado (atrasado)';
      }
      if (this.isEntregueNoPrazo(wf)) {
        return 'Finalizado (no prazo)';
      }
      return 'Finalizado';
    }
    return this.isAtrasadoEmAndamento(wf) ? 'Atrasado' : 'No prazo';
  }

  formatOsNumero(wf: WorkflowDetalhe): string {
    return wf.osNumero || `OS-${String(wf.id).padStart(6, '0')}`;
  }
}

